// src/containers/DynamoDBListingPage/DynamoDBListingPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, IconClose, Page, GetYourGuideWidget, getLocationIdFromAddress } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { fetchDynamoDBListingByIdAPI } from '../../util/api';

import css from './DynamoDBListingPage.module.css';

// Add breadcrumb styles to your CSS file:
/*
.breadcrumbs {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 24px;
}

.breadcrumbList {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  max-width: 1200px;
  margin: 0 auto;
}

.breadcrumbItem {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
}

.breadcrumbItem:not(:last-child)::after {
  content: '›';
  margin: 0 8px;
  color: #999;
  font-size: 12px;
}

.breadcrumbItem:last-child {
  color: #333;
  font-weight: 500;
}

.breadcrumbLink {
  color: #667eea;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumbLink:hover {
  color: #764ba2;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .breadcrumbs {
    padding: 8px 16px;
  }
  
  .breadcrumbItem {
    font-size: 12px;
  }
  
  .breadcrumbItem:not(:last-child)::after {
    margin: 0 4px;
  }
}
*/

const DynamoDBListingPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const dynamoId = id.replace('dynamo-', '');

    fetchDynamoDBListingByIdAPI(dynamoId)
      .then(data => {
        if (data.item) {
          setListing(data.item);
        } else {
          setError('Experience not found');
        }
        setIsLoading(false);
      })
      .catch(err => {
        console.error('Error fetching listing:', err);
        setError('Failed to load experience');
        setIsLoading(false);
      });
  }, [id]);

  // Generate structured data for SEO
  const generateStructuredData = () => {
    if (!listing) return null;

    const baseUrl = window.location.origin;
    const currentUrl = window.location.href;

    // Main product/service structured data
    const productData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: listing.title,
      description:
        listing.description ||
        `Experience ${listing.title} - Water sports and adventure activities`,
      url: currentUrl,
      image: listing.photoUrls || [],
      brand: {
        '@type': 'Brand',
        name: 'WetRentals',
      },
      offers: {
        '@type': 'Offer',
        price: listing.averagePrice ? listing.averagePrice.replace(/[^\d.]/g, '') : null,
        priceCurrency: listing.currency || 'USD',
        availability: 'https://schema.org/InStock',
        url: currentUrl,
        seller: {
          '@type': 'Organization',
          name: 'WetRentals',
        },
      },
    };

    // Add rating if available
    if (listing.rating && listing.reviewCount) {
      productData.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: listing.rating,
        reviewCount: listing.reviewCount,
        bestRating: 5,
        worstRating: 1,
      };
    }

    // Add location if available
    if (listing.location) {
      productData.locationCreated = {
        '@type': 'Place',
        name: `${listing.location.city || listing.location.destination}${
          listing.location.country ? `, ${listing.location.country}` : ''
        }`,
        address: {
          '@type': 'PostalAddress',
          addressLocality: listing.location.city || listing.location.destination,
          addressCountry: listing.location.country,
        },
      };
    }

    // Breadcrumb structured data
    const breadcrumbData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: baseUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Experiences',
          item: `${baseUrl}/s`,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: listing.title,
          item: currentUrl,
        },
      ],
    };

    return [productData, breadcrumbData];
  };

  const handleClose = () => {
    history.goBack();
  };

  const handleBookNow = () => {
    if (listing && listing.affiliateLink) {
      window.location.href = listing.affiliateLink;
    }
  };

  const openImageModal = index => {
    setCurrentImageIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const nextImage = () => {
    if (listing && listing.photoUrls) {
      setCurrentImageIndex(prev => (prev === listing.photoUrls.length - 1 ? 0 : prev + 1));
    }
  };

  const prevImage = () => {
    if (listing && listing.photoUrls) {
      setCurrentImageIndex(prev => (prev === 0 ? listing.photoUrls.length - 1 : prev - 1));
    }
  };

  const handleKeyDown = e => {
    if (showImageModal) {
      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showImageModal, listing]);

  if (isLoading) {
    return (
      <Page>
        <div className={css.container}>
          <div className={css.header}>
            <div className={css.topbar}>
              <TopbarContainer />
            </div>
            <button className={css.closeButton} onClick={handleClose}>
              <IconClose className={css.closeIcon} />
              <span className={css.closeText}>Close</span>
            </button>
          </div>

          <div className={css.loadingScreen}>
            <div className={css.loadingContent}>
              <img src="/static/logo-desktop.png" alt="WetRentals" className={css.logo} />
              <div className={css.loadingMessage}>
                <h2>Loading experience details...</h2>
                <div className={css.loadingSpinner}></div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Helmet>
          <title>Experience Not Found - WetRentals</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className={css.container}>
          <div className={css.header}>
            <div className={css.topbar}>
              <TopbarContainer />
            </div>
            <button className={css.closeButton} onClick={handleClose}>
              <IconClose className={css.closeIcon} />
              <span className={css.closeText}>Close</span>
            </button>
          </div>

          <div className={css.errorContainer}>
            <div className={css.errorContent}>
              <h1>Experience Unavailable</h1>
              <p>{error}</p>
              <Button onClick={handleClose}>Go Back</Button>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const structuredData = generateStructuredData();
  const locationString = listing?.location
    ? `${listing.location.city || listing.location.destination}${
        listing.location.country ? `, ${listing.location.country}` : ''
      }`
    : '';

  return (
    <Page>
      <Helmet>
        {/* Primary SEO tags */}
        <title>
          {listing?.title
            ? `${listing.title} - Water Sports Experience${
                locationString ? ` in ${locationString}` : ''
              } | WetRentals`
            : 'Experience Details | WetRentals'}
        </title>
        <meta
          name="description"
          content={
            listing?.description ||
            `Book ${listing?.title || 'this amazing water sports experience'}${
              locationString ? ` in ${locationString}` : ''
            }. ${
              listing?.rating ? `Rated ${listing.rating}/5 stars` : 'Highly rated'
            } water adventure with professional guides and safety equipment included.`
          }
        />

        {/* Open Graph tags */}
        <meta property="og:title" content={listing?.title || 'Water Sports Experience'} />
        <meta
          property="og:description"
          content={
            listing?.description ||
            `Amazing water sports experience${locationString ? ` in ${locationString}` : ''}`
          }
        />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />
        {listing?.photoUrls?.[0] && <meta property="og:image" content={listing.photoUrls[0]} />}
        <meta property="og:site_name" content="WetRentals" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={listing?.title || 'Water Sports Experience'} />
        <meta
          name="twitter:description"
          content={
            listing?.description ||
            `Amazing water sports experience${locationString ? ` in ${locationString}` : ''}`
          }
        />
        {listing?.photoUrls?.[0] && <meta name="twitter:image" content={listing.photoUrls[0]} />}

        {/* Additional SEO tags */}
        <meta
          name="keywords"
          content={`water sports, ${listing?.title ||
            'experience'}, ${locationString}, boat rental, water activities, adventure tourism`}
        />
        {/* Scraped third-party experience pages are intentionally kept out of
            search. The X-Robots-Tag header in server/index.js is the primary
            signal; this meta tag reinforces it for client-rendered views. */}
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={window.location.href} />

        {/* Structured Data */}
        {structuredData &&
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data, null, 2)}
            </script>
          ))}
      </Helmet>

      <div className={css.container}>
        {/* Header with close button */}
        <div className={css.header}>
          <div className={css.topbar}>
            <TopbarContainer />
          </div>
          <button className={css.closeButton} onClick={handleClose} aria-label="Close listing page">
            <IconClose className={css.closeIcon} />
            <span className={css.closeText}>Close</span>
          </button>
        </div>

        {/* Breadcrumbs */}
        <nav className={css.breadcrumbs} aria-label="Breadcrumb navigation">
          <ol className={css.breadcrumbList}>
            <li className={css.breadcrumbItem}>
              <Link to="/" className={css.breadcrumbLink}>
                Home
              </Link>
            </li>
            <li className={css.breadcrumbItem}>
              <Link to="/s" className={css.breadcrumbLink}>
                Experiences
              </Link>
            </li>

            <li className={css.breadcrumbItem} aria-current="page">
              {listing?.title}
            </li>
          </ol>
        </nav>

        {/* Listing Details */}
        <div className={css.content}>
          <div className={css.listingContainer}>
            {listing?.photoUrls && listing.photoUrls.length > 0 && (
              <div className={css.imageContainer}>
                <img
                  src={listing.photoUrls[0]}
                  alt={`${listing.title} - Main image`}
                  className={css.mainImage}
                  onClick={() => openImageModal(0)}
                />
                {listing.photoUrls.length > 1 && (
                  <div className={css.imageGallery}>
                    {listing.photoUrls.slice(1, 4).map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`${listing.title} - Image ${index + 2}`}
                        className={css.galleryImage}
                        onClick={() => openImageModal(index + 1)}
                      />
                    ))}
                    {listing.photoUrls.length > 4 && (
                      <div
                        className={css.moreImages}
                        onClick={() => openImageModal(4)}
                        role="button"
                        tabIndex={0}
                        aria-label={`View ${listing.photoUrls.length - 4} more images`}
                      >
                        +{listing.photoUrls.length - 4} more
                      </div>
                    )}
                  </div>
                )}

                <div className={css.bookingSection}>
                  <Button
                    onClick={handleBookNow}
                    className={css.bookNowButton}
                    size="large"
                    aria-label={`Book ${listing.title} with partner`}
                  >
                    Book Now with Partner
                  </Button>
                  <p className={css.partnerNote}>
                    You'll be redirected to our trusted partner to complete your booking
                  </p>
                </div>
              </div>
            )}

            <div className={css.listingDetails}>
              <header className={css.listingHeader}>
                <h1 className={css.title}>{listing?.title}</h1>
                {listing?.location && (
                  <p className={css.location}>
                    <span aria-label="Location">📍</span>{' '}
                    {listing.location.city || listing.location.destination}
                    {listing.location.country && `, ${listing.location.country}`}
                  </p>
                )}
              </header>

              {listing?.rating && (
                <div
                  className={css.ratingSection}
                  itemScope
                  itemType="https://schema.org/AggregateRating"
                >
                  <div className={css.rating}>
                    <span
                      className={css.ratingStars}
                      aria-label={`Rating: ${listing.rating} out of 5 stars`}
                    >
                      ⭐ <span itemProp="ratingValue">{listing.rating.toFixed(1)}</span>
                    </span>
                    {listing.reviewCount && (
                      <span className={css.reviewCount}>
                        (<span itemProp="reviewCount">{listing.reviewCount}</span> reviews)
                      </span>
                    )}
                    <meta itemProp="bestRating" content="5" />
                    <meta itemProp="worstRating" content="1" />
                  </div>
                </div>
              )}

              {listing?.averagePrice && (
                <div className={css.priceSection}>
                  <div className={css.price} itemScope itemType="https://schema.org/Offer">
                    <span className={css.priceAmount} itemProp="price">
                      {listing.averagePrice}
                    </span>
                    {listing.currency && (
                      <span className={css.priceUnit}>
                        <meta itemProp="priceCurrency" content={listing.currency} />
                        {listing.currency}
                      </span>
                    )}
                    <meta itemProp="availability" content="https://schema.org/InStock" />
                  </div>
                </div>
              )}

              {listing?.description && (
                <section className={css.description}>
                  <h2>About this experience</h2>
                  <p>{listing.description}</p>
                </section>
              )}

              <section className={css.features}>
                <h2>
                  {listing?.features && listing.features.length > 0
                    ? "What's included"
                    : 'Experience highlights'}
                </h2>
                <ul className={css.featuresList}>
                  {listing?.features && listing.features.length > 0 ? (
                    listing.features.map((feature, index) => (
                      <li key={index} className={css.feature}>
                        ✓ {feature}
                      </li>
                    ))
                  ) : (
                    <>
                      <li className={css.feature}>✓ Professional crew and guides</li>
                      <li className={css.feature}>✓ Safety equipment provided</li>
                      <li className={css.feature}>✓ Scenic water adventure</li>
                      {listing?.photoCount && (
                        <li className={css.feature}>
                          ✓ {listing.photoCount} stunning photo opportunities
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </section>
            </div>
          </div>

          {/* Partner Experiences Section - Outside the grid */}
          {listing?.location?.destination && getLocationIdFromAddress(listing.location.destination) && (
            <div className={css.partnerSection}>
              <h2 className={css.partnerSectionTitle}>
                Explore more experiences from our partner
              </h2>
              <GetYourGuideWidget
                className={css.partnerWidget}
                searchAddress={listing.location.destination}
              />
            </div>
          )}
        </div>

        {/* Image Modal */}
        {showImageModal && listing?.photoUrls && (
          <div
            className={css.imageModal}
            onClick={closeImageModal}
            role="dialog"
            aria-modal="true"
            aria-label="Image gallery"
          >
            <div className={css.modalContent} onClick={e => e.stopPropagation()}>
              <button
                className={css.modalClose}
                onClick={closeImageModal}
                aria-label="Close image gallery"
              >
                <IconClose />
              </button>

              <button className={css.modalPrev} onClick={prevImage} aria-label="Previous image">
                ‹
              </button>

              <img
                src={listing.photoUrls[currentImageIndex]}
                alt={`${listing.title} - Image ${currentImageIndex + 1} of ${
                  listing.photoUrls.length
                }`}
                className={css.modalImage}
              />

              <button className={css.modalNext} onClick={nextImage} aria-label="Next image">
                ›
              </button>

              <div className={css.imageCounter} aria-live="polite">
                {currentImageIndex + 1} / {listing.photoUrls.length}
              </div>

              <div className={css.thumbnailStrip} role="tablist" aria-label="Image thumbnails">
                {listing.photoUrls.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className={`${css.thumbnail} ${
                      index === currentImageIndex ? css.activeThumbnail : ''
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                    role="tab"
                    aria-selected={index === currentImageIndex}
                    tabIndex={index === currentImageIndex ? 0 : -1}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
};

export default DynamoDBListingPage;
